import axios from "axios";
import { getTokenHeader } from "@/helpers/commonHelper";
import { normalizeHtmlForDiff } from "@/helpers/textDiff";

const ResultTextPlaceholder =
  "<i>Noch kein Ergebnis – klicke auf »Erstellen«</i>";

export default {
  name: "RewriteTextDialog",
  props: {
    isOpen: {
      type: Boolean,
      default: false
    },
    originalText: {
      type: String,
      default: ""
    },
    targetKey: {
      type: String,
      default: "content"
    },
    module: {
      type: String,
      default: "news"
    },
    principalId: {
      type: String,
      required: true
    },
    apiUrl: {
      type: String,
      required: true
    },
    saveTargetOptions: {
      type: Array,
      default: () => []
    },
    mainPeacock: {
      type: String,
      default: "#00636C"
    }
  },
  data() {
    return {
      dialog: {
        loading: false,
        resultText: ResultTextPlaceholder,
        step: "form",
        diffPanel: null,
        diffChangeCount: 0,
        promptsLoading: false,
        promptOptions: [],
        selectedPrompt: null,
        flags: { allowLength: true, allowTone: true, allowAudience: true },
        length: 0,
        tone: "-",
        toneOptions: [
          "-",
          "Neutral",
          "Lebendig",
          "Sachlich",
          "Behördlich",
          "Marketing"
        ],
        audience: ["-"],
        audienceOptions: ["-", "Bürger allgemein", "Tourist", "Fachpublikum"],
        lengthLabels: ["Standard", "200", "400", "600", "800", "1000"]
      },
      editorUrl: "https://cdn.eberl-online.cloud/ckeditor5/ckeditor5.js",
      isResultTextGenerated: false,
      diffWordsLoadError: false
    };
  },
  computed: {
    internalOpen: {
      get() {
        return this.isOpen;
      },
      set(value) {
        if (!value) {
          this.closeDialog();
        }
        this.$emit("update:isOpen", value);
      }
    },
    diffSourceHtml() {
      return this.normalizeHtmlForDiff(this.originalText);
    },
    diffTargetHtml() {
      return this.isResultTextGenerated
        ? this.normalizeHtmlForDiff(this.dialog.resultText)
        : "";
    },
    isDiffPanelOpen() {
      return (
        this.dialog.diffPanel !== null && this.dialog.diffPanel !== undefined
      );
    },
    diffChangesLabel() {
      const count = Number(this.dialog.diffChangeCount) || 0;
      return `${count} ${count === 1 ? "Änderung" : "Änderungen"}`;
    }
  },
  watch: {
    isOpen(newVal) {
      if (newVal) {
        this.initializeDialog();
      }
    }
  },
  methods: {
    normalizeHtmlForDiff,

    async initializeDialog() {
      this.dialog.step = "form";
      this.dialog.diffPanel = null;
      this.dialog.diffChangeCount = 0;
      this.dialog.resultText = ResultTextPlaceholder;
      this.isResultTextGenerated = false;
      this.diffWordsLoadError = false;
      await this.fetchPrompts();
      this.selectDefaultPrompt();
    },

    closeDialog() {
      this.dialog.loading = false;
      this.dialog.step = "form";
      this.dialog.diffPanel = null;
      this.dialog.diffChangeCount = 0;
      this.dialog.resultText = ResultTextPlaceholder;
      this.isResultTextGenerated = false;
      this.diffWordsLoadError = false;
      this.dialog.promptOptions = [];
      this.dialog.selectedPrompt = null;
      this.$emit("close");
    },

    onDiffWordsError(error) {
      this.diffWordsLoadError = true;
      this.dialog.diffChangeCount = 0;
      this.$emit("error", {
        text: "Differenzansicht konnte nicht geladen werden.",
        error
      });
    },

    async fetchPrompts(force = false) {
      try {
        this.dialog.promptsLoading = true;
        const headers = getTokenHeader();
        const params = {
          principal_id: this.principalId,
          module: this.module,
          action: "rewrite",
          force
        };
        const res = await axios.get(`${this.apiUrl}api/eo-prompt/prompts`, {
          params,
          headers
        });
        this.dialog.promptOptions = (res.data || []).map(p => ({
          id: p._id,
          name: p.name,
          module: p.module,
          apiShortId: p.apiShortId,
          promptShortId: p.shortId,
          isDefault: p.isDefault
        }));
      } catch (e) {
        console.error("Fehler beim Laden der Prompts:", e);
        this.$emit("error", {
          text: "Keine Prompts gefunden.",
          error: e
        });
      } finally {
        this.dialog.promptsLoading = false;
      }
    },

    selectDefaultPrompt() {
      const prompts = this.dialog.promptOptions;
      if (!Array.isArray(prompts) || !prompts.length) return;

      const def = prompts.find(
        p => p.isDefault && (!p.module || p.module === this.module)
      );

      const selected = def || prompts[0];

      this.dialog.selectedPrompt = selected.id || selected._id;
      this.applyPromptDefaults(selected);
    },

    applyPromptDefaults(p) {
      this.dialog.flags = {
        ...this.dialog.flags,
        ...(p.flags || {})
      };
    },

    async createRewrite() {
      if (!this.originalText || !this.dialog.selectedPrompt) {
        return;
      }

      const p = this.dialog.promptOptions.find(
        x => x.id === this.dialog.selectedPrompt
      );
      if (!p) return;

      if (!p.apiShortId || !p.promptShortId) {
        this.$emit("error", {
          text: "Prompt-Konfiguration unvollständig."
        });
        return;
      }

      this.dialog.loading = true;
      this.dialog.diffPanel = null;
      this.dialog.diffChangeCount = 0;
      this.isResultTextGenerated = false;
      this.diffWordsLoadError = false;

      try {
        const headers = getTokenHeader();

        const {
          tone,
          length,
          audience,
          flags: { allowTone, allowLength, allowAudience }
        } = this.dialog;

        let payloadText = this.originalText;
        const metaHints = [];

        if (allowTone && tone && tone !== "-") {
          metaHints.push(`Tonalität: ${tone}`);
        }

        if (allowLength) {
          const lengthIndex = Number(length);
          const label = this.dialog.lengthLabels[lengthIndex];
          if (lengthIndex > 0 && label) {
            metaHints.push(`Gewünschte Länge: ${label}`);
          }
        }

        if (allowAudience) {
          const normalizedAudience = Array.isArray(audience)
            ? audience.filter(item => item && item !== "-")
            : audience && audience !== "-"
            ? [audience]
            : [];

          if (normalizedAudience.length) {
            metaHints.push(`Zielgruppe: ${normalizedAudience.join(", ")}`);
          }
        }

        if (metaHints.length) {
          payloadText = `${payloadText}\n\n${metaHints.join("\n")}`;
        }

        const body = {
          principal_id: this.principalId,
          apiShortId: p.apiShortId,
          promptShortId: p.promptShortId,
          payload: payloadText
        };

        const res = await axios.post(`${this.apiUrl}api/eo-prompt/exec`, body, {
          headers
        });
        this.dialog.resultText = res.data?.text || "";
        this.dialog.step = "result";
        this.isResultTextGenerated = true;

        this.$emit("text-generated", {
          text: this.dialog.resultText,
          targetKey: this.targetKey
        });
      } catch (e) {
        console.error(e);
        this.$emit("error", {
          text: "Text konnte nicht erzeugt werden.",
          error: e
        });
      } finally {
        this.dialog.loading = false;
      }
    },

    applyRewrite() {
      const text = this.dialog.resultText || "";
      if (!text) return;

      this.$emit("text-applied", {
        text,
        targetKey: this.targetKey
      });

      this.closeDialog();
    }
  }
};
